import React, { useState } from "react";
import BSButton from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Stack from "react-bootstrap/Stack";
import {
  useGetSeriousOffenseReasonsListQuery,
  useAddOrUpdateSeriousOffenseReasonMutation,
} from "features/api/contentManagement/contentManagementApiSlice";
import * as Yup from "yup";
import { PencilSquare } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { Button } from "./Button";
import RenderList from "./List/RenderList";
import AddContentForm from "./AddContentForm";
import {
  SeriousOffenseReason,
  SeriousOffenseReasonControlsValues,
  SeriousOffenseReasonTabFields,
} from "../contentManagementUtils/tabSchema";

const SeriousOffenseReasonsTab = () => {
  const { t } = useTranslation();
  const { data: reasons } = useGetSeriousOffenseReasonsListQuery();

  const [addOrUpdateSeriousOffenseReason] =
    useAddOrUpdateSeriousOffenseReasonMutation();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] =
    useState<SeriousOffenseReason | null>(null);

  const toggleCreateForm = () => {
    setShowCreateForm(!showCreateForm);
  };

  const toggleUpdateForm = (initialValues: SeriousOffenseReason | null) => {
    setShowUpdateForm(initialValues);
  };

  const validationSchema: Yup.ObjectSchema<SeriousOffenseReasonControlsValues> =
    Yup.object().shape({
      name: Yup.string().required(t("חובה להזין שם סיבת עבירה חמורה")),
      id: Yup.number().nullable(),
    });

  const initialValues: SeriousOffenseReason = {
    name: "",
    id: 0,
    disable: false,
    isEditable: true,
  };

  const handleCreateOrUpdate: (
    values: SeriousOffenseReasonControlsValues
  ) => void = async (values: SeriousOffenseReason) => {
    const payload = {
      name: values.name,
      id: values?.id ?? 0,
      disable: values?.disable ?? false,
    };

    await addOrUpdateSeriousOffenseReason(payload).unwrap();

    // close modal
    values?.id ? toggleUpdateForm(null) : toggleCreateForm();
  };

  const handleDisable = async (reason: SeriousOffenseReason) => {
    const payload = {
      name: reason.name,
      id: reason.id,
      disable: reason.disable,
    };

    await addOrUpdateSeriousOffenseReason(payload).unwrap();
  };

  const columns = [
    {
      title: t("מספר סיבת עבירה חמורה"),
      dataIndex: 1,
      key: "id",
      flex: 1,
      renderCell: (text: string) => <span>{text}</span>,
      asc: true,
      minWidth: 150,
    },
    {
      title: t("שם סיבת עבירה חמורה"),
      dataIndex: 2,
      key: "name",
      flex: 1,
      renderCell: (text: string) => <span>{text}</span>,
      asc: true,
      minWidth: 150,
    },
    {
      title: t("חסום?"),
      dataIndex: 3,
      key: "actions",
      flex: 1,
      renderCell: (reason: SeriousOffenseReason) => {
        const isLockedReason = !reason.isEditable;
        return (
          <Stack direction="horizontal" gap={0}>
            <Form.Check
              type="switch"
              id="custom-switch"
              defaultChecked={reason.disable}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleDisable({ ...reason, disable: event.target.checked });
              }}
              disabled={isLockedReason}
              size={20}
            />
          </Stack>
        );
      },
      asc: true,
      minWidth: 150,
    },
    {
      title: "",
      dataIndex: 4,
      key: "actions",
      flex: 1,
      renderCell: (reason: SeriousOffenseReason) => {
        const isLockedReason = !reason.isEditable;
        return (
          <Stack direction="horizontal" gap={0}>
            <div
              className={`button-wrapper ${reason.disable ? "disabled" : ""}`}
            >
              <BSButton
                key={reason.id}
                name={reason.name}
                variant="outline"
                disabled={isLockedReason}
                style={isLockedReason ? { border: "none", opacity: 0.5 } : {}}
              >
                <PencilSquare
                  size={20}
                  color={isLockedReason ? "gray" : "primary"}
                  onClick={() => {
                    if (!isLockedReason) {
                      toggleUpdateForm(reason);
                    }
                  }}
                />
              </BSButton>
            </div>
          </Stack>
        );
      },
      asc: true,
      minWidth: 150,
    },
  ];

  return (
    <div className="tab-list-container">
      <div className="tab-list-header">
        <Button title={t("add")} handleClick={toggleCreateForm} />
      </div>
      <RenderList<SeriousOffenseReason>
        data={reasons}
        columns={columns}
        disableField="disable"
      />
      <AddContentForm
        isOpen={showCreateForm}
        toggle={toggleCreateForm}
        fields={SeriousOffenseReasonTabFields}
        submitForm={handleCreateOrUpdate}
        headerTitle={t("סיבת עבירה חמורה חדשה")}
        approveBtnLabel={t("יצירת סיבת עבירה חמורה")}
        cancelBtnLabel={t("cancel")}
        initialValues={initialValues}
        validationSchema={validationSchema}
      />
      <AddContentForm
        isOpen={!!showUpdateForm}
        toggle={toggleUpdateForm}
        fields={SeriousOffenseReasonTabFields}
        submitForm={handleCreateOrUpdate}
        headerTitle={t("עדכן סיבת עבירה חמורה")}
        approveBtnLabel={t("עדכן סיבת עבירה חמורה")}
        cancelBtnLabel={t("cancel")}
        initialValues={showUpdateForm}
        validationSchema={validationSchema}
      />
    </div>
  );
};

export default SeriousOffenseReasonsTab;
